'use client';

import { motion, useInView } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useTransition } from 'react';
import { CircleCheck, RefreshCcw, TriangleAlert } from 'lucide-react';

import { DealerFormValues, dealerSchema } from '@/slw/entities/dealer-partner/dealer-partner.schema';
import { useSubmitDealerPartnerMutation } from '@/slw/entities/dealer-partner/dealer-partner.mutation';
import FormProvider, { Input, Textarea, Combobox } from '@/slw/shared/ui/form';
import { Button } from '@/slw/shared/ui/button';
import { useCheckZipCodeQuery, useCountries, useStatesByCountry } from '@/slw/entities/country/country-query';
import { useDebounce } from '@/slw/shared/hooks/use-debounce';
import { useAuthStore } from '@/slw/shared/stores/auth.store';
import { useSearchLocation } from '@/slw/entities/dealer-partner/dealer-partner.query';
import { FormLabel } from '@/slw/shared/ui/base-form';

export function DealerPartnerForm() {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const { user } = useAuthStore();
  const methods = useForm<DealerFormValues>({
    resolver: zodResolver(dealerSchema),
    defaultValues: {
      fullName: '',
      email: '',
      dealerShipName: '',
      preferredContactPhoneNumber: '',
      website: '',
      averageNumberOfVehiclesInInventory: '',
      address: '',
      countryCode: '',
      stateCode: '',
      zipCode: '',
      city: '',
      aboutBusiness: '',
      latitude: '',
      longitude: '',
    },
  });
  const [isPending, startTransition] = useTransition();

  const watchCountry = methods.watch('countryCode');
  const watchState = methods.watch('stateCode');
  // const { data: citysData } = useCitiesQuery(watchCountry);
  const { data: countriesData } = useCountries();
  const disabled = useMemo(() => !user?.id, [user]);
  const countrySlug = countriesData?.data?.find((country) => country.countryCode === watchCountry)?.countrySlug;

  const { data: statesData } = useStatesByCountry(countrySlug ?? '');

  const zipCode = methods.watch('zipCode');
  const zipCodeValue = useDebounce(zipCode, 1000);
  const { data: zipResult, isFetching: zipLoading } = useCheckZipCodeQuery(watchCountry, zipCodeValue);

  const { data: searchLocationData } = useSearchLocation({ country: watchCountry, state: watchState });

  const submitDealer = useSubmitDealerPartnerMutation();

  useEffect(() => {
    const places = zipResult?.data?.places;
    const message = zipResult?.message;
    const success = zipResult?.success;

    if (zipResult) {
      if (places && places.length > 0) {
        methods.setValue('city', places[0].city, { shouldValidate: true });
        success && methods.clearErrors('zipCode');
      } else {
        methods.setValue('city', '');
        !success && methods.setError('zipCode', { message });
      }
    }
  }, [zipResult, methods]);

  useEffect(() => {
    if (searchLocationData) {
      methods.setValue('longitude', searchLocationData.lon, { shouldValidate: true });
      methods.setValue('latitude', searchLocationData.lat, { shouldValidate: true });
    }
  }, [searchLocationData, methods]);

  const onSubmit = async (data: DealerFormValues) => {
    startTransition(async () => {
      try {
        const submitData = {
          ...data,
          ecomUserId: user?.id,
          averageNumberOfVehiclesInInventory: Number(data.averageNumberOfVehiclesInInventory) || 0,
        };

        await submitDealer.mutateAsync(submitData);
        methods.reset();
      } catch (error) {
        // Error handled
      }
    });
  };

  return (
    <FormProvider {...methods} onSubmit={onSubmit} className='md:mb-20 mb-10'>
      <div className='mb-8 text-center'>
        <motion.h3
          className='text-3xl md:text-[2.5rem] leading-[120%] font-bold mb-8'
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          Unlock Your Exclusive Benefits
        </motion.h3>
      </div>
      <div ref={ref} className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4'>
        {/* Row 1: Full Name, Email Address */}

        <Input disabled={disabled} direction='column' name='fullName' label='Full Name' required />
        <Input disabled={disabled} name='email' direction='column' label='Email Address' type='email' required />
        {/* Row 2: Dealership Name, Phone */}
        <Input disabled={disabled} direction='column' name='dealerShipName' label='Dealership Name' />
        <Input
          disabled={disabled}
          direction='column'
          required
          name='preferredContactPhoneNumber'
          label='Preferred Contact Phone Number'
        />
        {/* Row 3: Website, Avg Vehicles */}
        <Input disabled={disabled} direction='column' name='website' label='Website' />
        <Input
          disabled={disabled}
          direction='column'
          required
          name='averageNumberOfVehiclesInInventory'
          label='Average Number of Vehicles in Inventory'
        />
        {/* Row 4: Address, Country, State, ZIP, City (5 columns) */}
        <div className='grid grid-cols-1 sm:grid-cols-4 md:grid-cols-8 gap-x-4 gap-y-4 md:col-span-2'>
          <Input
            disabled={disabled}
            containerClass='md:col-span-4 sm:col-span-4 col-span-1'
            direction='column'
            name='address'
            label='Address'
            required
          />
          <Combobox
            required
            direction='column'
            name='countryCode'
            label='Country'
            disabled={disabled}
            containerClass='sm:col-span-2 col-span-1'
            options={
              countriesData?.data?.map((country) => ({ label: country.country, value: country.countryCode })) || []
            }
            onValueChange={(val) => {
              methods.setValue('countryCode', val);
              methods.setValue('city', '');
              methods.setValue('stateCode', '');
            }}
            placeholder='Select'
          />

          <Combobox
            direction='column'
            name='stateCode'
            label='State'
            containerClass='sm:col-span-2 col-span-1'
            required
            options={statesData?.data?.map((state) => ({ label: state.state, value: state.stateCode })) || []}
            placeholder='Select'
            disabled={!methods.watch('countryCode') || disabled}
          />
          <div className='flex flex-col gap-1 sm:col-span-2 col-span-1'>
            <div className='flex items-center justify-between mb-1'>
              <FormLabel className='whitespace-nowrap font-normal text-sm leading-[1.5]'>
                ZIP code <span className='text-destructive'>*</span>
              </FormLabel>

              {zipLoading ? (
                <RefreshCcw size={16} className='animate-spin' />
              ) : zipResult?.success ? (
                <CircleCheck size={16} className='text-success' />
              ) : zipResult?.success === false ? (
                <TriangleAlert size={16} className='text-warning' />
              ) : null}
            </div>
            <Input
              required
              direction='column'
              label=''
              name='zipCode'
              placeholder=''
              disabled={!methods.watch('countryCode') || disabled}
            />
          </div>
          <Input
            containerClass='sm:col-span-2 col-span-1'
            direction='column'
            name='city'
            disabled={true}
            label='City'
            required
          />

          <Input
            containerClass='sm:col-span-2 col-span-1'
            direction='column'
            name='longitude'
            disabled={true}
            label='Longitude'
            required
          />

          <Input
            containerClass='sm:col-span-2 col-span-1'
            direction='column'
            name='latitude'
            disabled={true}
            label='Latitude'
            required
          />
        </div>
        {/* Row 5: Textarea (full width) */}
        <div className='grid grid-cols-2 md:col-span-2 col-span-1'>
          <Textarea
            disabled={disabled}
            name='aboutBusiness'
            direction='column'
            label='Tell us more about your business'
            containerClass='col-span-2 lg:col-span-1'
            required
            rows={8}
            placeholder="Describe your business, specialties, inventory, and anything else you'd like us to know."
          />
        </div>
      </div>
      <div className='flex relative flex-col sm:flex-row  gap-4 justify-center mt-8  items-center'>
        <Button
          type='submit'
          color='primary'
          variant='default'
          style={{ minWidth: 180 }}
          disabled={isPending || disabled}
        >
          {isPending ? 'Submitting...' : 'Submit'}
        </Button>
        <p className='text-xs sm:absolute left-0 leading-[140%] text-foreground'>
          Note: You will need to login before submit
        </p>
      </div>
    </FormProvider>
  );
}
